import { useState, useEffect } from "react";
import { Row, Col, Form, Button, Badge, InputGroup, Card } from "react-bootstrap";
import AddEditUserModal from "./AddEditUserModal";
import { mockUsers } from "services/auth";
import { FaUsers, FaChalkboardTeacher, FaShieldAlt, FaTrash, FaSearch, FaKey, FaSchool } from "react-icons/fa";
import DataTable from "views/Components/DataTable";
import ClipLoader from "react-spinners/ClipLoader";
import { CiMail } from "react-icons/ci";
import { MdPeople } from "react-icons/md";
import ManagementHeader from "views/Components/ManagementHeader";
import { UserRightsModal } from "./permissionModal/user-rights-modal";
import { toast } from "react-toastify";
import SpecificAPI from "services/SpecificApi";
import DeleteModal from "views/Components/DeleteModal";
import CustomSelect from "views/Components/CustomSelect";
import { useUserStore } from "context/useUserStore";
import { USER_ROLES } from "services/constants";
import localization from "services/localization";
import { FaPencil } from "react-icons/fa6";
import moment from "moment";

import { useSchoolsStore } from "context/useSchoolsStore";
import { useLanguageStore } from "context/useLanguageStore";

export default function StaffManagement() {
  const { currentLang } = useLanguageStore();

  const [staffMembers, setStaffMembers] = useState([]);
  const [selectedRole, setSelectedRole] = useState({
    value: "all",
    label: localization.all || "All",
  });
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentStaff, setCurrentStaff] = useState({});
  const [errors, setErrors] = useState({});
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRightsModal, setUserRightsModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemName, setItemName] = useState("");
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [theme, setTheme] = useState(localStorage.getItem("SA_user_theme") || "light");
  const { userData } = useUserStore();
  const { schools } = useSchoolsStore();

  const emptyUser = {
    name: "",
    email: "",
    role: "student",
    // password: "",
    school_id: userData?.selected_school_id ? [userData?.selected_school_id] : [],
  };

  const ROLE_COLORS = {
    teacher: {
      bg: "bg-blue-100 border-blue-300",
      text: "text-blue-700",
      badge: "primary",
      label: `${localization.teacher || "Teacher"}`,
    },
    student: {
      bg: "bg-green-100 border-green-300",
      text: "text-green-700",
      badge: "success",
      label: `${localization.student || "Student"}`,
    },
    administrative: {
      bg: "bg-purple-100 border-purple-300",
      text: "text-purple-700",
      badge: "secondary",
      label: `${localization.administration || "Administration"}`,
    },
    root: {
      bg: "bg-red-100 border-red-300",
      text: "text-red-700",
      badge: "danger",
      label: `${localization.super_admin || "Super Admin"}`,
    },
  };

  const columns = [
    {
      label: `${localization.name || "Name"}`,
      key: "name",
      width: "250px",
      render: (value, row) => (
        <div className="d-flex align-items-center gap-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
            style={{
              width: "40px",
              minWidth: "40px",
              height: "40px",
              backgroundColor: "var(--primary-action)",
            }}
          >
            {getInitials(row.name)}
          </div>
          <div>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              className="small"
            >
              {row.name}
            </span>
            {/* <p className="mb-0 fw-medium">{row.name}</p> */}
            {/* <p className="mb-0 small text-muted">{row.id}</p> */}
          </div>
        </div>
      ),
    },
    {
      label: `${localization.email || "Email"}`,
      key: "email",
      width: "250px",
      render: (value) => (
        <div
          key={value}
          className="d-flex align-items-center gap-2 mb-1"
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={value}
        >
          <CiMail className="text-muted" size={16} />
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
            className="small"
          >
            {value}
          </span>
        </div>
        // <div className="d-flex align-items-center gap-2">
        //   <CiMail className="text-muted" size={16} />
        //   <span>{value}</span>
        // </div>
      ),
    },
    {
      label: `${localization.role || "Role"}`,
      key: "role",
      width: "200px",
      render: (value) => (
        <Badge bg={ROLE_COLORS[value]?.badge || "secondary"} className="px-3 py-2" style={{ color: "white" }}>
          {ROLE_COLORS[value]?.label || value}
        </Badge>
      ),
    },
    {
      label: `${localization.organizations || "Organizations"}`,
      key: "schools",
      render: (_, row) => {
        if (!row.school_id || row.school_id.length === 0) {
          return <span className="text-muted">No Schools</span>;
        }

        return (
          <div
            style={{
              maxWidth: "200px", // adjust width as needed
            }}
          >
            {row.school_id.map((schoolId) => {
              const schoolName = schools.find((school) => school.id === schoolId)?.name || "Unknown School";

              return (
                <div
                  key={schoolId}
                  className="d-flex align-items-center gap-2 mb-1"
                  style={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  title={schoolName}
                >
                  <FaSchool size={12} className="text-primary flex-shrink-0" />
                  <span
                    style={{
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    className="small"
                  >
                    {schoolName}
                  </span>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      label: `${localization.actions || "Actions"}`,
      key: "actions",
      render: (_, row) => (
        <div className="d-flex justify-content-center gap-2">
          <Button
            className="action-btn-outline success"
            size="sm"
            onClick={() => handleResetPassword(row)}
            title="Reset Password"
          >
            <FaKey />
          </Button>
          {/* <Button
            className="action-btn-outline success"
            size="sm"
            onClick={() => handleShowUserRightModal(row)}
            title="User Rights"
          >
            <FaShieldAlt />
          </Button> */}
          <Button className="action-btn-outline primary" size="sm" onClick={() => handleShowModal(row)} title="Edit">
            <FaPencil />
          </Button>
          <Button className="action-btn-outline danger" size="sm" onClick={() => handleDelete(row)} title="Delete">
            <FaTrash />
          </Button>
        </div>
      ),
    },
  ];

  useEffect(() => {
    filterStaff();
  }, [searchTerm, staffMembers, selectedRole]);

  useEffect(() => {
    getAllData();
  }, []);

  useEffect(() => {
    setTheme(localStorage.getItem("SA_user_theme") || "light");
  }, [theme]);

  const getAllData = async () => {
    setLoading(true);
    try {
      const response = await SpecificAPI.getAllUsers();
      setStaffMembers(response || []);
    } catch (err) {
      toast.error("Failed to fetch staff members.");
      console.error("Error fetching staff members:", err);
    } finally {
      setLoading(false);
    }
  };

  const filterStaff = () => {
    let filtered = staffMembers;
    const term = searchTerm.trim().toLowerCase();

    if (term) {
      filtered = filtered.filter((user) => {
        const nameMatch = user.name?.toLowerCase().includes(term);
        const emailMatch = user.email?.toLowerCase().includes(term);

        return nameMatch || emailMatch;
      });
    }

    if (selectedRole.value !== "all") {
      filtered = filtered.filter((cls) => cls.role?.toLowerCase() === selectedRole.value.toLowerCase());
    }

    setFilteredStaff(filtered);
  };

  const handleShowModal = (staff = null) => {
    setCurrentStaff(staff || emptyUser);
    setErrors({});
    setShowModal(true);
  };

  const handleShowUserRightModal = (row) => {
    setUserRightsModal(true);
    setSelectedUser(row.id);
  };

  const handleResetPassword = (row) => {
    SpecificAPI.sendPasswordReset(row.email)
      .then((res) => {
        toast.success("Password reset email sent successfully.");
      })
      .catch((err) => {
        console.error("Error sending password reset email:", err);
        toast.error("Failed to send password reset email.");
      });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentStaff(emptyUser);
    setErrors({});
  };

  // const validatePassword = (password) => {
  //   const regex =
  //     /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+[\]{};':"\\|,.<>/?]).{8,}$/;
  //   return regex.test(password);
  // };

  const validateForm = (form) => {
    const e = {};

    if (!form.name || !form.name.trim()) {
      e.name = localization.name_required || "Name is required";
    }
    if (!form.email || !form.email.trim()) {
      e.email = localization.email_required || "Email is required";
    }

    if (!form.birthdate || !form.birthdate?.trim()) {
      e.birthdate = localization.birthdate_required || "Birthdate is required";
    }

    if (!form.role || !form.role.trim()) {
      e.role = localization.role_required || "Role is required";
    }
    // if (!form.id && !validatePassword(form.password)) {
    //   e.password =
    //     localization.password_validation ||
    //     "Password must be at least 8 characters and include uppercase, lowercase, number, and special character.";
    // }

    return e;
  };

  const ensureWalletSchool = async (userId, schoolIds) => {
    const walletRes = await SpecificAPI.getAllWallets({ user_id: userId });
    let wallet = walletRes?.[0];

    const defaultCredit = (schoolId) => ({
      school_id: schoolId,
      credit_available: 0,
      credit_frozen: 0,
      credit_spent: 0,
    });

    // CASE 1: create
    if (!wallet) {
      return SpecificAPI.createWalletRecord({
        user_id: userId,
        credits: schoolIds.map(defaultCredit),
      });
    }

    // CASE 2: update existing
    if (!Array.isArray(wallet.credits)) {
      wallet.credits = [];
    }

    const existing = new Set(wallet.credits.map((c) => c.school_id));

    const missing = schoolIds.filter((id) => !existing.has(id));

    if (missing.length === 0) return wallet;

    wallet.credits.push(...missing.map(defaultCredit));

    return SpecificAPI.updateWalletRecord(wallet);
  };

  const handleSubmit = async (data) => {
    const validationErrors = validateForm(data);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    try {
      setLoading(true);

      if (currentStaff?.id) {
        const res = await SpecificAPI.updateUser(currentStaff.id, {
          ...data,
          _id: currentStaff.id,
        });

        await ensureWalletSchool(currentStaff?.id, res?.data?.school_id);

        setStaffMembers((prev) => prev.map((m) => (m.id === currentStaff.id ? res.data : m)));
      } else {
        const created = await SpecificAPI.createUser(data);
        const resetPW = await SpecificAPI.sendPasswordReset(data.email);

        await ensureWalletSchool(created.data.id, created?.data?.school_id);

        if (created.data.role === "teacher") {
          created?.data?.school_id?.forEach(async (schoolId) => {
            const createUtilityDepartment = await SpecificAPI.createDepartment({
              name: "Utilities",
              description: null,
              type: "utilities",
              start_date: moment().format("YYYY-MM-DD"),
              end_date: moment().format("YYYY-MM-DD"),
              module_id: ["68dfcbc48564db56450f14d2", "69660dd6b1687daa180ca322"],
              school_id: schoolId,
              user_id: created.data.id,
            });
          });
        }

        setStaffMembers((prev) => [...prev, created.data]);
      }
      setShowModal(false);
      setCurrentStaff(emptyUser);
      toast.success(localization.user_saved || "User saved successfully!");
    } catch (err) {
      console.error("Error saving item:", err);
      toast.error(err?.response?.data?.error || localization.user_save_failed || "Failed to save user!");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (user) => {
    setItemToDelete(user);
    setItemName(user.name);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;

    try {
      setLoading(true);
      await SpecificAPI.deleteUser(itemToDelete.id);

      setStaffMembers((prev) => prev.filter((ev) => ev.id !== itemToDelete.id));

      toast.success("User deleted successfully");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete user. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setItemToDelete(null);
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };
  const handleChange = (e) => {
    const { name, value } = e.target;

    setCurrentStaff((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // if (loading)
  //   return (
  //     <div
  //       className="d-flex justify-content-center align-items-center"
  //       style={{ minHeight: "300px" }}
  //     >
  //       <ClipLoader color="#0d6efd" size={50} />
  //     </div>
  //   );

  const totalStudents = staffMembers.filter((s) => s.role === "student").length;
  const totalTeachers = staffMembers.filter((s) => s.role === "teacher").length;
  const totalStaff = staffMembers.length - (totalStudents + totalTeachers);

  return (
    <div style={{ padding: "3rem", height: "90vh" }}>
      {/* Header Section */}
      <ManagementHeader
        title={localization.staff_management || "Staff Management"}
        subtitle={`Manage ${staffMembers.length} staff members across ${USER_ROLES.length - 1} roles`}
        // icon={MdPeople}
        buttonText={localization.add_staff || "Add Staff"}
        onButtonClick={() => handleShowModal()}
        showBack={true}
        backTo="/schools"
        backLabel="Back to organization"
      />

      <Row className="mb-4 staff-management-stats">
        <Col md={3} sm={6} className="mb-3">
          <Card className="shadow-sm border-0 p-3 d-flex flex-column align-items-center justify-content-center text-center">
            <FaUsers size={30} className="icon-btn mb-2" />
            <h5>{localization.total_users || "Total Users"}</h5>
            <h3>{staffMembers.length}</h3>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="shadow-sm border-0 p-3 d-flex flex-column align-items-center justify-content-center text-center">
            <FaUsers size={30} className="title-icon mb-2" />
            <h5>{localization.total_students || "Total Students"}</h5>
            <h3>{totalStudents}</h3>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="shadow-sm border-0 p-3 d-flex flex-column align-items-center justify-content-center text-center">
            <FaChalkboardTeacher size={30} className="text-danger mb-2" />
            <h5>{localization.total_teachers || "Total Teachers"}</h5>
            <h3>{totalTeachers}</h3>
          </Card>
        </Col>
        <Col md={3} sm={6} className="mb-3">
          <Card className="shadow-sm border-0 p-3 d-flex flex-column align-items-center justify-content-center text-center">
            <FaUsers size={30} className="text-warning mb-2" />
            <h5>{localization.total_staff || "Total Staff"}</h5>
            <h3>{totalStaff}</h3>
          </Card>
        </Col>
      </Row>
      <Card className="border-0 shadow-sm staff-card">
        <Card.Header className="border-0 py-3">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-5">
              <h5 className="mb-0 font-bold">{localization.all_staff || "All Staff"}</h5>
              <div className="d-flex align-items-center gap-3 mt-3">
                <InputGroup className="search-field" style={{ width: "25rem" }}>
                  <InputGroup.Text id="basic-addon1" className="customSearchAddon">
                    <FaSearch className="icon-btn" />
                  </InputGroup.Text>
                  <Form.Control
                    type="text"
                    placeholder={localization.search_name_email || "Search by name or email"}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    aria-describedby="basic-addon1"
                    className="customSearchInput"
                  />
                </InputGroup>
              </div>
              <div className="align-items-center gap-3 mt-3" style={{ flex: "0 0 250px" }}>
                <CustomSelect
                  options={USER_ROLES}
                  value={selectedRole}
                  onChange={(opt) => setSelectedRole(opt)}
                  placeholder={localization.select_role || "Select a role"}
                />
              </div>
            </div>
            <Badge pill className="badge-color px-3 py-2 rounded-pill shadow-sm">
              {filteredStaff.length}
            </Badge>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <ClipLoader size={40} color="var(--primary-icon)" />
            </div>
          ) : (
            <DataTable columns={columns} data={filteredStaff} onEdit={handleShowModal} onDelete={handleDelete} />
          )}
        </Card.Body>
      </Card>

      {/* Modal */}
      <AddEditUserModal
        title={currentStaff?.id ? localization.edit_user || "Edit User" : localization.add_user || "Add User"}
        isOpen={showModal}
        onClose={handleCloseModal}
        onSave={handleSubmit}
        onChange={handleChange}
        formData={currentStaff}
        errors={errors}
        loading={loading}
      />

      {selectedUser && (
        <UserRightsModal
          isOpen={userRightsModal}
          onClose={() => {
            setUserRightsModal(false);
            setSelectedUser(null);
          }}
          userId={selectedUser}
          userName={mockUsers.find((u) => u.id === selectedUser)?.name || ""}
        />
      )}
      <DeleteModal
        show={showDeleteModal}
        itemName={itemName}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        loading={loading}
        setLoading={setLoading}
      />
    </div>
  );
}
